import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, LogOut, Settings as SettingsIcon } from 'lucide-react';
import HubTileArt, { getTileInnerStyle } from '../components/HubTileArt';
import { useLimitedShell } from './LimitedShellContext';
import {
  getVisibleLimitedPages,
  getVisibleExternalLinks,
  isRouteActive,
  isExternalUrl,
} from './limitedPages';
import {
  GLASS_STYLE,
  GLASS_CARD_STYLE,
  ENCON_ORANGE,
  ENCON_BLACK,
  MUTED_TEXT,
  QUICK_LINK_MIME,
  PINNED_MIME,
} from './limitedStyles';

const hasDragType = (event, type) => Array.from(event.dataTransfer?.types || []).includes(type);

// One glass tile: the same illustrated art the Home page tiles use, with a small label under it.
// Some tiles (the M4.5 Pills that don't have bespoke illustrations) use a lucide `icon` instead.
const RailTile = ({ label, artKey, icon: Icon, active, onClick, testId, onRemove, dragProps }) => (
  <div className="relative group shrink-0" {...(dragProps || {})}>
    <button
      type="button"
      onClick={onClick}
      title={label}
      data-testid={testId}
      className="flex flex-col items-center gap-0.5 w-[52px] focus:outline-none"
    >
      <span
        className="relative flex items-center justify-center w-[42px] h-[42px] rounded-xl overflow-hidden transition-transform duration-200 group-hover:scale-105"
        style={{
          ...getTileInnerStyle(artKey, 'light'),
          outline: active ? `2px solid ${ENCON_ORANGE}` : 'none',
          outlineOffset: '1px',
        }}
      >
        {Icon
          ? <Icon className="w-5 h-5" style={{ color: active ? ENCON_ORANGE : ENCON_BLACK }} />
          : <HubTileArt artKey={artKey} theme="light" className="absolute inset-0 w-full h-full" />}
      </span>
      <span
        className="text-[9px] leading-tight font-bold text-center w-full line-clamp-2 break-words"
        style={{ color: active ? ENCON_ORANGE : ENCON_BLACK }}
      >
        {label}
      </span>
    </button>
    {onRemove && (
      <button
        type="button"
        onClick={(event) => { event.stopPropagation(); onRemove(); }}
        title="Remove from sidebar"
        data-testid={`${testId}-remove`}
        className="absolute -top-1 right-0 w-4 h-4 rounded-full bg-white shadow items-center justify-center hidden group-hover:flex"
      >
        <X className="w-3 h-3" style={{ color: ENCON_ORANGE }} />
      </button>
    )}
  </div>
);

const Divider = () => (
  <span
    aria-hidden="true"
    className="shrink-0 w-px h-8 md:w-8 md:h-px"
    style={{ background: 'rgba(238,114,3,0.25)' }}
  />
);

const LimitedSidebar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pinnedLinks, pinLink, unpinLink, movePinned, openLimitedModal } = useLimitedShell();
  const [dropActive, setDropActive] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const pages = getVisibleLimitedPages(user);
  const externals = getVisibleExternalLinks(user);
  const canOpenSettings = !!user && (user.role === 'super_admin' || !!user.can_access_settings);

  // Close the settings menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocMouseDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpen]);

  // Desktop: the panel's height grows with its content, so the menu's position is measured off
  // the panel itself (rather than a hardcoded offset) each time it opens.
  useEffect(() => {
    if (!menuOpen || !navRef.current || window.innerWidth < 768) { setMenuAnchor(null); return; }
    const rect = navRef.current.getBoundingClientRect();
    setMenuAnchor({ top: rect.bottom + 8, left: rect.left });
  }, [menuOpen]);

  const openLink = (link) => {
    if (isExternalUrl(link.url)) window.open(link.url, '_blank', 'noopener,noreferrer');
    else navigate(link.url);
  };

  // Drop zone for pinned quick links (accepts a link dragged from the Quick Links card, or a
  // pinned tile being re-ordered).
  const onZoneDragOver = (event) => {
    if (hasDragType(event, QUICK_LINK_MIME) || hasDragType(event, PINNED_MIME)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (!dropActive) setDropActive(true);
    }
  };
  const onZoneDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setDropActive(false);
  };
  const onZoneDrop = (event) => {
    event.preventDefault();
    setDropActive(false);
    const quickLinkId = event.dataTransfer.getData(QUICK_LINK_MIME);
    const pinnedId = event.dataTransfer.getData(PINNED_MIME);
    if (quickLinkId) pinLink(quickLinkId);
    else if (pinnedId) movePinned(pinnedId, null);
  };

  return (
    <>
      <nav
        ref={navRef}
        data-testid="limited-sidebar"
        aria-label="Limited access navigation"
        className="c30-limited-scroll fixed z-40 flex flex-row gap-1.5 p-1.5 rounded-2xl items-center overflow-x-auto overflow-y-hidden bottom-2 left-2 right-2 h-[80px] md:top-[68px] md:bottom-auto md:right-auto md:left-3 md:w-[60px] md:h-auto md:flex-col md:overflow-visible"
        style={GLASS_STYLE}
      >
        {/* Home - always first */}
        <RailTile
          label="Home"
          artKey="home"
          active={location.pathname === '/limited'}
          onClick={() => navigate('/limited')}
          testId="limited-nav-home"
        />

        {/* Site navigation - the rolled-out pages this user has permission for */}
        {pages.map((page) => (
          <RailTile
            key={page.id}
            label={page.label}
            artKey={page.artKey}
            icon={page.icon}
            active={page.kind === 'route' && isRouteActive(location.pathname, page.route)}
            onClick={() => (page.kind === 'modal' ? openLimitedModal(page.modalKey) : navigate(page.route))}
            testId={`limited-nav-${page.id}`}
          />
        ))}

        {/* CRM and Staffology - the links from the Home page cards, permission based */}
        {externals.map((link) => (
          <RailTile
            key={link.id}
            label={link.label}
            artKey={link.artKey}
            active={false}
            onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
            testId={`limited-nav-${link.id}`}
          />
        ))}

        <Divider />

        {/* Pinned quick links - saved per user, drag them in from the Quick Links card */}
        <div
          data-testid="limited-pinned-zone"
          className="flex flex-row md:flex-col items-center gap-1.5 rounded-xl p-1 min-h-[42px] min-w-[42px] shrink-0"
          style={dropActive ? { outline: `2px dashed ${ENCON_ORANGE}`, background: 'rgba(238,114,3,0.08)' } : undefined}
          onDragOver={onZoneDragOver}
          onDragLeave={onZoneDragLeave}
          onDrop={onZoneDrop}
        >
          {pinnedLinks.length === 0 && (
            <span
              className="text-[9px] leading-tight text-center font-semibold w-[52px]"
              style={{ color: MUTED_TEXT }}
            >
              Drop links here
            </span>
          )}
          {pinnedLinks.map((link) => (
            <RailTile
              key={link.id}
              label={link.name}
              artKey="quick-link"
              active={false}
              onClick={() => openLink(link)}
              testId={`limited-pinned-${link.id}`}
              onRemove={() => unpinLink(link.id)}
              dragProps={{
                draggable: true,
                onDragStart: (event) => {
                  event.dataTransfer.setData(PINNED_MIME, link.id);
                  event.dataTransfer.setData('text/plain', link.name);
                  event.dataTransfer.effectAllowed = 'move';
                },
                onDrop: (event) => {
                  const draggedId = event.dataTransfer.getData(PINNED_MIME);
                  if (draggedId) {
                    event.preventDefault();
                    event.stopPropagation();
                    setDropActive(false);
                    movePinned(draggedId, link.id);
                  }
                },
              }}
            />
          ))}
        </div>

        {/* Pushes Settings to the far end of the mobile bottom bar; desktop wraps naturally. */}
        <div className="flex-1 min-w-0 min-h-0 md:hidden" aria-hidden="true" />

        {/* Settings - always last */}
        <RailTile
          label="Settings"
          artKey="settings"
          active={menuOpen || location.pathname.startsWith('/settings')}
          onClick={() => setMenuOpen((open) => !open)}
          testId="limited-nav-settings"
        />
      </nav>

      {/* Settings menu. Rendered outside the nav because the nav's backdrop-filter would otherwise
          trap and clip a fixed-position child. Desktop position is measured off the floating panel
          (auto height, grows with its own content); mobile stays anchored above the bottom bar. */}
      {menuOpen && (
        <div
          ref={menuRef}
          data-testid="limited-settings-menu"
          className="fixed z-50 w-52 rounded-2xl p-1.5 bottom-[92px] right-2 md:bottom-auto md:right-auto md:left-3"
          style={{
            ...GLASS_CARD_STYLE,
            ...(menuAnchor ? { top: `${menuAnchor.top}px`, left: `${menuAnchor.left}px`, bottom: 'auto' } : {}),
          }}
        >
          {canOpenSettings && (
            <button
              type="button"
              onClick={() => { navigate('/settings'); setMenuOpen(false); }}
              data-testid="limited-menu-settings"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-white/80 transition-colors"
              style={{ color: ENCON_BLACK }}
            >
              <SettingsIcon className="w-4 h-4" style={{ color: ENCON_ORANGE }} />
              Settings
            </button>
          )}
          <button
            type="button"
            onClick={() => { setMenuOpen(false); if (onLogout) onLogout(); }}
            data-testid="limited-menu-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold hover:bg-red-50 transition-colors"
            style={{ color: '#b91c1c' }}
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      )}
    </>
  );
};

export default LimitedSidebar;

